// Dados da Jornada das 12 Emoções — Diagnóstico Saúde e Emoções
// Metodologia Trindade de Cura, por Mônica Maria.
// Cada vivência combina um óleo essencial com um processo terapêutico guiado em
// 4 perguntas, cada uma respondida em 3 campos: Eu reconheço que..., Eu agradeço
// por... e Eu sou..., sempre finalizando com "Eu sou o Eu Sou."

export interface PerguntaVivencia {
  numero: number
  pergunta: string
  ajudaReconheco: string
  ajudaAgradeco: string
  ajudaSou: string
}

export interface IndicacaoFisica {
  titulo: string
  texto: string
}

export interface Vivencia {
  numero: number
  oleo: string
  emoji: string
  titulo: string
  tituloCompleto: string
  tema: string
  indicadoPara: string
  promove: string
  comoUsarAntes: string
  exemploReconheco: string
  exemploAgradeco: string
  exemploSou: string
  perguntas: PerguntaVivencia[]
  orientacaoPratica: string
  indicacoesFisicas: IndicacaoFisica[]
  placeholder: string
}

export const VIVENCIAS: Vivencia[] = [
  {
    numero: 1,
    oleo: 'Lavanda',
    emoji: '💜',
    titulo: 'Comunicação e Calma',
    tituloCompleto: 'Lavanda, Comunicação e Calma',
    tema: 'liberar bloqueios de comunicação e criar um espaço interno de segurança e calma',
    indicadoPara: 'Comunicação bloqueada, medo de rejeição, tensão, sentimento de não ter voz e de não ser amado ou ouvido.',
    promove: 'Calma, amor, paz de espírito, sensação de bem-estar, comunicação aberta, expressividade, honestidade emocional e autoconsciência.',
    comoUsarAntes: 'Coloque o óleo na palma da mão, feche os olhos e inale profundamente. Sinta o campo se abrindo para a comunicação honesta.',
    exemploReconheco: 'Eu reconheço que tenho medo de falar o que sinto de verdade porque temo não ser aceito.',
    exemploAgradeco: 'Eu agradeço pela minha voz livre, pela minha autenticidade e pela coragem de me expressar com amor.',
    exemploSou: 'Eu sou voz, presença e verdade. Eu sou amado e ouvido em minha plena expressão. Eu sou o Eu Sou.',
    perguntas: [
      {
        numero: 1,
        pergunta: 'Existe algo que você carrega guardado há muito tempo, uma verdade, um sentimento, uma dor, que ainda não encontrou voz para ser dita? O que é?',
        ajudaReconheco: 'Nomeie o que está guardado. Vá fundo. Diga o que nunca disse.',
        ajudaAgradeco: 'Escreva sobre sua abertura, sua autenticidade, sua coragem de se expressar.',
        ajudaSou: 'Declare quem você é quando sua voz está livre e verdadeira.',
      },
      {
        numero: 2,
        pergunta: 'Com quem você sente que não consegue ser completamente honesto? O que teme que aconteça se for?',
        ajudaReconheco: 'Nomeie a pessoa e o medo real por trás do silêncio.',
        ajudaAgradeco: 'Escreva sobre sua capacidade de criar vínculos verdadeiros e seguros.',
        ajudaSou: 'Declare quem você é como ser que se comunica com amor e clareza.',
      },
      {
        numero: 3,
        pergunta: 'Em que momentos da sua vida você silenciou sua voz por medo de não ser aceito ou amado? Como isso ainda vive em você?',
        ajudaReconheco: 'Reconheça os momentos e o que ficou vivo dentro de você.',
        ajudaAgradeco: 'Escreva sobre o amor que você merece receber sendo exatamente quem você é.',
        ajudaSou: 'Declare quem você é como voz que tem o direito de existir e ser ouvido.',
      },
      {
        numero: 4,
        pergunta: 'O que sua voz interior mais quer dizer neste momento, sobre você, sobre sua vida, sobre o que você deseja?',
        ajudaReconheco: 'Deixe a voz interior falar sem filtro. Escreva tudo.',
        ajudaAgradeco: 'Escreva sobre a paz que existe quando você vive alinhado com sua verdade interior.',
        ajudaSou: 'Declare quem você é quando está em plena expressão de si mesmo.',
      },
    ],
    orientacaoPratica: 'Inale 1 gota de Lavanda sempre que sentir que sua voz está presa, que as palavras não saem, que o medo de se expor está presente. Use também antes de conversas decisivas, apresentações importantes ou simplesmente para ativar sua autenticidade e sua presença.',
    indicacoesFisicas: [
      { titulo: 'Aromático', texto: 'Inalar 1 gota nas mãos sempre que sentir desconforto emocional, antes de dormir ou em momentos de tensão.' },
      { titulo: 'Tópico', texto: 'Diluir 1 gota de Lavanda com 10 gotas de óleo de coco fracionado. Aplicar na nuca, pulso e área do coração para equilíbrio emocional e pressão alta. Aplicar diluído na área afetada para feridos, queimaduras e irritações na pele.' },
      { titulo: 'Cortes leves', texto: '1 gota pura no local.' },
      { titulo: 'Tensão e insônia', texto: '1 gota sublingual.' },
      { titulo: 'Precauções', texto: 'Seguro para uso adulto nas formas aromática, tópica e interna. Gestantes e lactantes devem usar apenas nas formas tópica e aromática com cautela.' },
    ],
    placeholder: 'Escreva livremente o que vier à mente...',
  },
  {
    numero: 2,
    oleo: 'Hortelã-Pimenta',
    emoji: '🌿',
    titulo: 'Um Coração Flutuante',
    tituloCompleto: 'Hortelã-Pimenta, Um Coração Flutuante',
    tema: 'transformar a dor emocional em ânimo, energia e alegria renovada',
    indicadoPara: 'Dor insuportável, desespero intenso, aflição, pessimismo, tristeza, peso emocional e sensação de afundamento.',
    promove: 'Otimismo, alívio, força para encarar a dor emocional, ânimo, energia, disposição e alegria renovada.',
    comoUsarAntes: 'Coloque o óleo na palma da mão, feche os olhos e inale profundamente. Sinta o frescor abrindo o peito e trazendo ar e vida.',
    exemploReconheco: 'Eu reconheço que carrego um peso emocional enorme e que não consigo enxergar saída para o que estou sentindo.',
    exemploAgradeco: 'Eu agradeço pela minha força, pela minha ousadia e pela alegria que está ativa agora e para sempre a partir de mim.',
    exemploSou: 'Eu sou força, renovação e leveza. Eu sou um coração que flutua e segue. Eu sou o Eu Sou.',
    perguntas: [
      {
        numero: 1,
        pergunta: 'Qual é a dor emocional que você carrega há mais tempo e que ainda pesa no seu coração? O que ela faz com você quando aparece?',
        ajudaReconheco: 'Nomeie a dor com coragem. Diga o que ela faz com você, como ela vive no seu corpo e na sua vida.',
        ajudaAgradeco: 'Escreva sobre sua força, sua capacidade de se renovar e sua resiliência.',
        ajudaSou: 'Declare quem você é além dessa dor, quem você é quando ela não define mais você.',
      },
      {
        numero: 2,
        pergunta: 'Em que momento da sua vida você se sentiu mais desesperançoso? Esse sentimento ainda influencia suas escolhas hoje?',
        ajudaReconheco: 'Reconheça o momento e a forma como ele ainda opera dentro de você.',
        ajudaAgradeco: 'Escreva sobre a esperança que existe em você, sobre o que te move mesmo nos dias difíceis.',
        ajudaSou: 'Declare quem você é como ser que encontra luz mesmo nas sombras.',
      },
      {
        numero: 3,
        pergunta: 'O que você ainda não permitiu que doesse de verdade? O que está guardado com força para não sentir?',
        ajudaReconheco: 'Nomeie o que está reprimido. Dê permissão para que apareça agora, com segurança.',
        ajudaAgradeco: 'Escreva sobre sua capacidade de sentir, processar e se renovar através da dor.',
        ajudaSou: 'Declare quem você é como ser que tem coragem de sentir e ainda assim segue em frente.',
      },
      {
        numero: 4,
        pergunta: 'Se sua dor pudesse falar, o que ela diria que ainda precisa ser ouvido?',
        ajudaReconheco: 'Dê voz à dor. Ouça o que ela carrega. Escreva sem filtro.',
        ajudaAgradeco: 'Escreva sobre a cura que acontece quando você se permite ouvir e acolher sua própria dor.',
        ajudaSou: 'Declare quem você é como ser que transforma dor em sabedoria e em caminho.',
      },
    ],
    orientacaoPratica: 'Inale 1 gota de Hortelã-Pimenta sempre que sentir o peso emocional chegando, quando o pessimismo ou a tristeza quiserem tomar conta. Inale também nos momentos em que precisar de energia para seguir, de ânimo para recomeçar, de leveza quando tudo parecer pesado demais. Recomenda-se o uso apenas durante o dia para não interferir no sono.',
    indicacoesFisicas: [
      { titulo: 'Aromático', texto: 'Inalar 1 gota nas mãos para concentração, energia, criatividade e alegria. Usar somente durante o dia.' },
      { titulo: 'Tópico', texto: 'Diluir 1 gota com 10 gotas de óleo de coco fracionado. Aplicar na área afetada para dores de cabeça, enxaqueca e dores musculares. Aplicar diluído no peito, costas ou sola dos pés para desconfortos respiratórios.' },
      { titulo: 'Febre', texto: '1 gota diluída na sola dos pés.' },
      { titulo: 'Digestivo', texto: 'Ingerir: pingue 1 gota em uma colher e tome com água. Se preferir, use uma cápsula vegetal. Ou aplicar diluído na área do estômago.' },
      { titulo: 'Precauções', texto: 'Hipertensos e gestantes usar com cautela. Epiléticos e lactantes não devem usar. Usar somente durante o dia.' },
    ],
    placeholder: 'Escreva livremente o que vier à mente...',
  },
  {
    numero: 3,
    oleo: 'Limão Siciliano',
    emoji: '🍋',
    titulo: 'Foco',
    tituloCompleto: 'Limão Siciliano, Foco',
    tema: 'dissolver a confusão mental e trazer clareza, foco e presença',
    indicadoPara: 'Confusão mental, incapacidade de foco, fadiga mental, falta de alegria e energia, culpa e desocupação.',
    promove: 'Foco, energia, clareza mental, alerta, racionalidade, alegria e presença no momento.',
    comoUsarAntes: 'Coloque o óleo na palma da mão, feche os olhos e inale profundamente. Sinta a mente clarear e o pensamento se organizar.',
    exemploReconheco: 'Eu reconheço que estou disperso, sem foco, e que a confusão mental me impede de agir com clareza.',
    exemploAgradeco: 'Eu agradeço pela minha clareza, pelo meu foco preciso e pela minha capacidade de tomar decisões alinhadas.',
    exemploSou: 'Eu sou mente clara, presença plena e ação direcionada. Eu sou o Eu Sou.',
    perguntas: [
      {
        numero: 1,
        pergunta: 'Em que área da sua vida você se sente mais disperso, sem direção ou sem clareza do que realmente quer?',
        ajudaReconheco: 'Nomeie a área e a confusão que existe nela. Seja específico.',
        ajudaAgradeco: 'Escreva sobre sua capacidade de encontrar clareza, de tomar decisões alinhadas com quem você é.',
        ajudaSou: 'Declare quem você é como ser focado, presente e direcionado.',
      },
      {
        numero: 2,
        pergunta: 'Qual é a culpa que mais rouba sua energia mental e impede você de estar presente no que importa?',
        ajudaReconheco: 'Nomeie a culpa. De onde ela vem? O que ela ainda exige de você?',
        ajudaAgradeco: 'Escreva sobre sua capacidade de aprender com o passado sem ser aprisionado por ele.',
        ajudaSou: 'Declare quem você é como ser que aprende, evolui e segue livre de culpa.',
      },
      {
        numero: 3,
        pergunta: 'O que você fica adiando porque a mente não consegue se assentar no que é essencial? O que a dispersão está te custando?',
        ajudaReconheco: 'Reconheça o padrão de dispersão e o que ele está te fazendo perder.',
        ajudaAgradeco: 'Escreva sobre sua presença, sua capacidade de escolher e de agir com intencionalidade.',
        ajudaSou: 'Declare quem você é como ser que age com clareza, foco e propósito.',
      },
      {
        numero: 4,
        pergunta: 'Se você tivesse clareza total agora sobre o próximo passo da sua vida, qual seria ele? O que impede de você vê-lo com nitidez?',
        ajudaReconheco: 'Nomeie o que nubla a visão: o medo, a dúvida, o excesso de opções.',
        ajudaAgradeco: 'Escreva sobre sua intuição, sua inteligência e sua capacidade de enxergar o caminho.',
        ajudaSou: 'Declare quem você é como ser que vê com clareza e age com confiança.',
      },
    ],
    orientacaoPratica: 'Inale 1 gota de Limão Siciliano antes de estudar, trabalhar, tomar decisões importantes ou sempre que a mente estiver turva e dispersa. Recomenda-se o uso apenas durante o dia para não interferir no sono. Use também quando precisar de alegria e leveza — esse óleo clareia não só o pensamento, mas o humor.',
    indicacoesFisicas: [
      { titulo: 'Aromático', texto: 'Inalar 1 gota para foco, concentração, energia, disposição e clareza mental.' },
      { titulo: 'Interno', texto: 'Ingerir: pingue 1 gota em uma colher e tome com água, 2 a 3 vezes ao dia por 7 dias para imunidade. Se preferir, use uma cápsula vegetal. Aromatizar água em garrafa de vidro (1 a 3 gotas), nunca em garrafa de plástico.' },
      { titulo: 'Precauções', texto: 'O Limão Siciliano é fotossensível. Evitar aplicar na pele antes de exposição solar, pois pode causar manchas.' },
    ],
    placeholder: 'Escreva livremente o que vier à mente...',
  },
  {
    numero: 4,
    oleo: 'Melaleuca',
    emoji: '🌿',
    titulo: 'Limites Energéticos',
    tituloCompleto: 'Melaleuca, Limites Energéticos',
    tema: 'estabelecer limites energéticos claros e conexões saudáveis',
    indicadoPara: 'Relacionamentos parasitas e codependentes, limites indefinidos, desânimo, esgotamento, toxicidade emocional e impotência.',
    promove: 'Limites energéticos claros, conexões saudáveis e respeitosas, potência, resiliência e segurança.',
    comoUsarAntes: 'Coloque o óleo na palma da mão, feche os olhos e inale profundamente. Sinta a proteção e procure observar seus sentimentos.',
    exemploReconheco: 'Eu reconheço que permito que certas pessoas drenem minha energia porque não sei como estabelecer limites sem me sentir culpado.',
    exemploAgradeco: 'Eu agradeço pela minha energia abundante, pelos meus limites claros e pelas conexões que me nutrem e me respeitam.',
    exemploSou: 'Eu sou campo protegido, energia preservada e presença que atrai o que é verdadeiro e saudável. Eu sou o Eu Sou.',
    perguntas: [
      {
        numero: 1,
        pergunta: 'Existe alguém na sua vida que drena sua energia de forma consistente? O que acontece em você quando está perto dessa pessoa?',
        ajudaReconheco: 'Nomeie a pessoa e o que ela ativa em você: o cansaço, o esvaziamento, a obrigação.',
        ajudaAgradeco: 'Escreva sobre sua capacidade de se proteger, de escolher com quem você compartilha sua energia.',
        ajudaSou: 'Declare quem você é como ser que tem energia abundante e sabe preservá-la.',
      },
      {
        numero: 2,
        pergunta: 'Em que situações você diz sim quando quer dizer não? O que te impede de estabelecer limites claros?',
        ajudaReconheco: 'Reconheça os padrões. Nomeie o medo ou a crença que te faz ceder.',
        ajudaAgradeco: 'Escreva sobre sua integridade, seu direito de dizer não com amor e sem culpa.',
        ajudaSou: 'Declare quem você é como ser que estabelece limites com clareza, segurança e amor.',
      },
      {
        numero: 3,
        pergunta: 'Qual relacionamento da sua vida você sente que não consegue colocar limites ou não consegue expressar verdadeiramente pensamentos e sentimentos?',
        ajudaReconheco: 'Nomeie o relacionamento e o padrão que se repete nele.',
        ajudaAgradeco: 'Escreva sobre a conexão saudável que você merece, baseada em respeito mútuo.',
        ajudaSou: 'Declare quem você é como ser íntegro, autônomo e capaz de amar sem se perder.',
      },
      {
        numero: 4,
        pergunta: 'O que você continua alimentando — uma situação, uma relação, um papel — mesmo sabendo que está te esgotando?',
        ajudaReconheco: 'Nomeie o que você ainda alimenta mesmo exausto. Por quê?',
        ajudaAgradeco: 'Escreva sobre sua força de liberar o que não serve mais, com gratidão e sem culpa.',
        ajudaSou: 'Declare quem você é como ser que se renova, escolhe o que nutre e libera o que esgota.',
      },
    ],
    orientacaoPratica: 'Inale 1 gota de Melaleuca ou aplique 1 gota diluída com 5 gotas de óleo de coco fracionado na nuca sempre que se sentir invadido energeticamente, após encontros que drenam sua energia ou ao fim do dia para fazer uma limpeza energética. Esse óleo é seu guardião de campo.',
    indicacoesFisicas: [
      { titulo: 'Tópico', texto: 'Diluir 1 gota em 10 gotas de óleo de coco fracionado. Aplicar diluído na área afetada para irritações, feridos e manchas na pele.' },
      { titulo: 'Fungo na unha', texto: '1 gota pura no local.' },
      { titulo: 'Acne', texto: '1 gota diretamente no local.' },
      { titulo: 'Interno', texto: 'Ingerir: pingue 1 gota em uma colher e tome com água, 3 vezes ao dia por até 10 dias para inflamações e infecções. Se preferir, use uma cápsula vegetal.' },
    ],
    placeholder: 'Escreva livremente o que vier à mente...',
  },
  {
    numero: 5,
    oleo: 'Olíbano',
    emoji: '🟤',
    titulo: 'Verdade',
    tituloCompleto: 'Olíbano, Verdade',
    tema: 'reconectar-se com a verdade de si e com a proteção divina',
    indicadoPara: 'Abandono, desconexão espiritual, distanciamento do pai, desproteção, treva espiritual e desalinhamento com a verdade de si.',
    promove: 'Esclarecimento, amor, proteção, sabedoria, discernimento, espiritualidade aberta e conexão com o sagrado.',
    comoUsarAntes: 'Coloque o óleo na palma da mão, feche os olhos e inale profundamente. Sinta a conexão com algo maior e a intenção de se alinhar com sua verdade mais profunda.',
    exemploReconheco: 'Eu reconheço que me sinto desconectado do sagrado e que carrego uma sensação de abandono espiritual.',
    exemploAgradeco: 'Eu agradeço pela minha conexão profunda com o universo, pela proteção divina que me abraça e pela sabedoria que me guia.',
    exemploSou: 'Eu sou ser espiritual, amado, protegido e guiado. Eu sou verdade e luz. Eu sou o Eu Sou.',
    perguntas: [
      {
        numero: 1,
        pergunta: 'Qual é a mentira mais sutil que você conta para si mesmo, sobre quem você é, sobre o que merece, sobre o que é capaz?',
        ajudaReconheco: 'Nomeie a mentira interior com coragem. Ela pode ser sutil, mas é poderosa.',
        ajudaAgradeco: 'Escreva sobre sua capacidade de viver na verdade, sobre o alinhamento interno que te liberta.',
        ajudaSou: 'Declare quem você é quando está em pleno alinhamento com sua verdade mais profunda.',
      },
      {
        numero: 2,
        pergunta: 'Você se sente conectado a algo maior que você, seja Deus, o universo, sua espiritualidade? O que bloqueia essa conexão?',
        ajudaReconheco: 'Reconheça a desconexão e o que a gerou: dúvida, mágoa, distanciamento.',
        ajudaAgradeco: 'Escreva sobre a proteção, o amor e o suporte que você recebe do campo espiritual.',
        ajudaSou: 'Declare quem você é como ser espiritual, conectado, amado e protegido.',
      },
      {
        numero: 3,
        pergunta: 'Existe alguma ferida relacionada à figura do pai, seja ele físico ou simbólico, que ainda opera dentro de você?',
        ajudaReconheco: 'Nomeie a ferida. Como ela se manifesta nas suas relações e nas suas escolhas?',
        ajudaAgradeco: 'Escreva sobre o amor paterno que você merece receber, de dentro ou de fora.',
        ajudaSou: 'Declare quem você é como ser amado, protegido e digno de receber cuidado.',
      },
      {
        numero: 4,
        pergunta: 'Se você soubesse com absoluta certeza que está protegido e amado pelo universo, o que mudaria na forma como você vive?',
        ajudaReconheco: 'Reconheça o que o medo de não ser protegido te impede de fazer ou ser.',
        ajudaAgradeco: 'Escreva sobre a proteção divina e o suporte invisível que sempre esteve com você.',
        ajudaSou: 'Declare quem você é como ser protegido, guiado e sustentado pelo amor universal.',
      },
    ],
    orientacaoPratica: 'Inale 1 gota de Olíbano quando se sentir perdido, desorientado ou desconectado de quem você realmente é. Esse óleo é sagrado. Inale também antes de meditação, oração ou conexão espiritual.',
    indicacoesFisicas: [
      { titulo: 'Tópico', texto: 'Diluir 1 gota com 10 gotas de óleo de coco fracionado. Aplicar diluído na área afetada para dores, inflamações e manchas na pele.' },
      { titulo: 'Meditação', texto: '1 gota no topo da cabeça ou na testa para meditação e conexão espiritual.' },
      { titulo: 'Interno', texto: 'Ingerir 1 gota sublingual, 2 a 3 vezes ao dia, sempre após as refeições, para ansiedade, depressão, estresse e inflamações. Para potencializar, combinar com Copaíba.' },
    ],
    placeholder: 'Escreva livremente o que vier à mente...',
  },
  {
    numero: 6,
    oleo: 'Laranja Selvagem',
    emoji: '🍊',
    titulo: 'Abundância',
    tituloCompleto: 'Laranja Selvagem, Abundância',
    tema: 'liberar bloqueios para receber e reconectar-se com a alegria e a criatividade',
    indicadoPara: 'Sobrecarga de responsabilidade, criatividade sufocada, sentimento de obrigação, excesso de trabalho, aflição e falta de alegria.',
    promove: 'Criatividade, espontaneidade, diversão, realização, coração tranquilo, alegria, felicidade e otimismo.',
    comoUsarAntes: 'Coloque o óleo na palma da mão, feche os olhos e inale profundamente.',
    exemploReconheco: 'Eu reconheço que estou sobrecarregado de responsabilidades e que perdi o prazer de simplesmente ser e criar.',
    exemploAgradeco: 'Eu agradeço pela minha abundância, pela minha alegria natural e pela criatividade que flui em mim com leveza.',
    exemploSou: 'Eu sou abundância, criatividade e alegria plena. Eu sou leve, livre e cheia de vida. Eu sou o Eu Sou.',
    perguntas: [
      {
        numero: 1,
        pergunta: 'Em que área da sua vida você se sente mais bloqueado para receber, seja amor, dinheiro, oportunidades ou alegria?',
        ajudaReconheco: 'Nomeie o bloqueio. O que você acredita que precisa fazer ou ser para merecer receber?',
        ajudaAgradeco: 'Escreva sobre seu merecimento natural, sobre a abundância que já existe ao seu redor.',
        ajudaSou: 'Declare quem você é como ser que recebe com facilidade, graça e gratidão.',
      },
      {
        numero: 2,
        pergunta: 'Qual obrigação ou responsabilidade está sufocando sua alegria de viver? O que você faz por dever que já não faz por amor?',
        ajudaReconheco: 'Nomeie o que está pesando e o que a alegria perdeu dentro dessa obrigação.',
        ajudaAgradeco: 'Escreva sobre sua liberdade, sua leveza, seu direito de viver com prazer e espontaneidade.',
        ajudaSou: 'Declare quem você é como ser leve, criativo e cheio de vida.',
      },
      {
        numero: 3,
        pergunta: 'Quando foi a última vez que você se permitiu brincar, criar ou simplesmente ser sem objetivo? O que impede isso hoje?',
        ajudaReconheco: 'Reconheça o que você perdeu da espontaneidade e por que ela foi embora.',
        ajudaAgradeco: 'Escreva sobre sua capacidade de criar, de se divertir e de viver com leveza.',
        ajudaSou: 'Declare quem você é como ser espontâneo, criativo e repleto de alegria.',
      },
      {
        numero: 4,
        pergunta: 'O que a abundância significa para você, além de dinheiro? Em quais formas de abundância você ainda não se permite acreditar?',
        ajudaReconheco: 'Nomeie as formas de abundância que você ainda não reconhece ou não acredita merecer.',
        ajudaAgradeco: 'Escreva sobre a riqueza que já existe na sua vida, em todas as suas formas.',
        ajudaSou: 'Declare quem você é como ser abundante, próspero e grato em todas as dimensões da vida.',
      },
    ],
    orientacaoPratica: 'Inale 1 gota de Laranja Selvagem sempre que precisar de alegria, leveza ou criatividade. Use antes de atividades criativas ou simplesmente quando o dia estiver pesado e você precisar lembrar que a vida pode ser leve.',
    indicacoesFisicas: [
      { titulo: 'Aromático', texto: 'Inalar 1 gota para alegria, calma e alívio de ansiedade, medo e estresse.' },
      { titulo: 'Interno', texto: 'Ingerir: pingue 1 gota em uma colher e tome com água, 2 a 3 vezes ao dia para imunidade. Se preferir, use uma cápsula vegetal. Para uma noite repousante, 1 gota após o jantar. Aromatizar água em garrafa de vidro (1 a 3 gotas), nunca em garrafa de plástico.' },
      { titulo: 'Precauções', texto: 'A Laranja Selvagem é fotossensível. Evitar aplicar na pele antes de exposição solar.' },
    ],
    placeholder: 'Escreva livremente o que vier à mente...',
  },
  {
    numero: 7,
    oleo: 'Copaíba',
    emoji: '🌳',
    titulo: 'Revelação',
    tituloCompleto: 'Copaíba, Revelação',
    tema: 'liberar vergonha e culpa e reconhecer o próprio merecimento',
    indicadoPara: 'Vergonha, culpa, arrependimento, sentimento de inferioridade, não merecimento, emoções suprimidas e rebeldia.',
    promove: 'Merecimento, autoconsciência, clareza, perdão, redefinição de si mesmo e propósito existencial.',
    comoUsarAntes: 'Dilua 1 gota de Copaíba com 10 gotas de óleo de coco fracionado e aplique sobre o coração, pulsos e nuca.',
    exemploReconheco: 'Eu reconheço que carrego vergonha de partes de mim que acredito serem indignas de ser vistas.',
    exemploAgradeco: 'Eu agradeço pelo meu perdão, pela minha capacidade de me redefinir e pelo meu merecimento natural de ser amado e visto.',
    exemploSou: 'Eu sou digna, inteiro e luminosa. Eu sou livre da vergonha e em paz com minha história. Eu sou o Eu Sou.',
    perguntas: [
      {
        numero: 1,
        pergunta: 'Qual é a maior vergonha que você carrega, algo sobre quem você foi, o que fez ou o que não conseguiu ser?',
        ajudaReconheco: 'Nomeie a vergonha com coragem e compaixão por você mesma. Ela precisa ser vista para ser liberada.',
        ajudaAgradeco: 'Escreva sobre seu perdão a si mesmo, sobre sua capacidade de se redefinir além do passado.',
        ajudaSou: 'Declare quem você é hoje, livre da vergonha, inteiro e em paz com sua história.',
      },
      {
        numero: 2,
        pergunta: 'Existe um arrependimento que você ainda carrega e que te impede de se sentir merecedor do que deseja?',
        ajudaReconheco: 'Nomeie o arrependimento. O que ele te diz que você não merece?',
        ajudaAgradeco: 'Escreva sobre o aprendizado que esse erro te trouxe e sobre o seu direito de recomeçar.',
        ajudaSou: 'Declare quem você é como ser que aprendeu, cresceu e merece tudo que a vida tem a oferecer.',
      },
      {
        numero: 3,
        pergunta: 'O que você ainda suprime dentro de você, uma emoção, uma verdade, um desejo, por medo de parecer fraca, exagerada ou egoísta?',
        ajudaReconheco: 'Nomeie o que está suprimido. Dê espaço para que apareça aqui com segurança.',
        ajudaAgradeco: 'Escreva sobre sua inteireza, sobre seu direito de sentir, expressar e existir plenamente.',
        ajudaSou: 'Declare quem você é como ser inteiro, autêntico e sem nada a esconder.',
      },
      {
        numero: 4,
        pergunta: 'Se você se permitisse se ver sem o filtro da culpa ou da vergonha, quem você descobriria que é?',
        ajudaReconheco: 'Reconheça o que a culpa e a vergonha têm escondido de você sobre si mesmo.',
        ajudaAgradeco: 'Escreva sobre a luz que existe em você além dos erros, além das histórias do passado.',
        ajudaSou: 'Declare quem você é em sua essência mais verdadeira e mais luminosa.',
      },
    ],
    orientacaoPratica: 'Aplique 1 gota de Copaíba com 10 gotas de óleo de coco fracionado sobre o coração sempre que a vergonha ou a culpa aparecerem. Esse óleo é seu companheiro de revelação. Use também em momentos de introspecção e antes de sessões terapêuticas.',
    indicacoesFisicas: [
      { titulo: 'Tópico', texto: 'Diluir 1 gota com 10 gotas de óleo de coco fracionado. Aplicar diluído no local afetado para dores e inflamações.' },
      { titulo: 'Interno', texto: 'Ingerir 1 gota sublingual, 2 a 3 vezes ao dia, sempre após as refeições, para ansiedade, depressão, estresse e inflamações. Para potencializar, combinar com Olíbano.' },
    ],
    placeholder: 'Escreva livremente o que vier à mente...',
  },
  {
    numero: 8,
    oleo: 'On Guard',
    emoji: '🛡️',
    titulo: 'Proteção',
    tituloCompleto: 'On Guard, Proteção',
    tema: 'estabelecer limites saudáveis e fortalecer a proteção interior',
    indicadoPara: 'Hostilidade, falta de proteção, vulnerabilidade, controle, manipulação e suscetibilidade à pressão alheia.',
    promove: 'Limites saudáveis, proteção, capacidade, integridade, independência, reforço e fortalecimento.',
    comoUsarAntes: 'Dilua 1 gota de On Guard em 20 gotas de óleo de coco fracionado e aplique na área do estômago. Nunca inale puro nas mãos.',
    exemploReconheco: 'Eu reconheço que me sinto vulnerável e que permito que pessoas ultrapassem meus limites por medo de conflito.',
    exemploAgradeco: 'Eu agradeço pela minha força, pela minha integridade e pela proteção que existe naturalmente dentro de mim.',
    exemploSou: 'Eu sou protegido, forte e soberana. Meu campo é sagrado e ninguém o atravessa sem minha permissão. Eu sou o Eu Sou.',
    perguntas: [
      {
        numero: 1,
        pergunta: 'Em que situações ou com quais pessoas você se sente mais vulnerável, controlado ou manipulado?',
        ajudaReconheco: 'Nomeie as situações e as pessoas. Reconheça onde você perde sua força.',
        ajudaAgradeco: 'Escreva sobre sua potência, sua capacidade de se manter inteiro mesmo diante da pressão.',
        ajudaSou: 'Declare quem você é como ser protegido, forte e impossível de ser manipulado.',
      },
      {
        numero: 2,
        pergunta: 'Existe alguém que exerce poder sobre você de forma que você não gosta mas ainda permite? O que te impede de mudar isso?',
        ajudaReconheco: 'Reconheça o padrão e o medo que sustenta a permissão.',
        ajudaAgradeco: 'Escreva sobre sua independência, sua força e seu direito de viver sem controle externo.',
        ajudaSou: 'Declare quem você é como ser livre, soberano e capaz de se proteger com amor.',
      },
      {
        numero: 3,
        pergunta: 'Onde na sua vida você tem cedido sua integridade por medo de conflito, de desaprovação ou de perder algo?',
        ajudaReconheco: 'Nomeie o que você tem cedido e o que isso tem te custado.',
        ajudaAgradeco: 'Escreva sobre sua integridade, sobre o que significa viver alinhado com seus valores.',
        ajudaSou: 'Declare quem você é como ser íntegro que não negocia sua essência por aprovação.',
      },
      {
        numero: 4,
        pergunta: 'O que seria diferente na sua vida se você se sentisse completamente protegido e seguro por dentro?',
        ajudaReconheco: 'Reconheça o que o medo de não ser protegido tem te impedido de fazer ou ser.',
        ajudaAgradeco: 'Escreva sobre a segurança interior que nenhuma circunstância externa pode tirar de você.',
        ajudaSou: 'Declare quem você é como ser que carrega sua proteção por dentro, sempre.',
      },
    ],
    orientacaoPratica: 'Aplique 1 gota do On Guard diluído com 20 gotas de óleo de coco fracionado no abdômen sempre que se sentir ameaçado, pressionado ou vulnerável energeticamente. Use também ao entrar em ambientes ou reuniões onde a dinâmica pode ser desafiadora.',
    indicacoesFisicas: [
      { titulo: 'Interno', texto: 'Ingerir: pingue 1 gota em uma colher e tome com água após a refeição, 1 vez ao dia para imunidade. Para infecções, 3 vezes ao dia por 7 dias. Se preferir, use uma cápsula vegetal.' },
      { titulo: 'Tópico', texto: 'Diluir 1 gota com 10 a 20 gotas de óleo de coco fracionado, preferindo a sola dos pés.' },
      { titulo: 'Precauções', texto: 'Nunca inalar puro nas mãos. Usar apenas no difusor para aromatização. Pode manchar a pele exposta ao sol.' },
    ],
    placeholder: 'Escreva livremente o que vier à mente...',
  },
  {
    numero: 9,
    oleo: 'Deep Blue',
    emoji: '💙',
    titulo: 'Rendição à Dor',
    tituloCompleto: 'Deep Blue, Rendição à Dor',
    tema: 'encarar a dor emocional com serenidade, sem resistência e sem se destruir',
    indicadoPara: 'Resistência à dor, bloqueio para encarar problemas emocionais, pânico, medo, mágoa e machucado não processado.',
    promove: 'Força, aceitação, acalento, serenidade e a capacidade de encarar a dor sem se destruir.',
    comoUsarAntes: 'Dilua 1 gota de Deep Blue com 10 gotas de óleo de coco fracionado e aplique na nuca, ombros e base da coluna.',
    exemploReconheco: 'Eu reconheço que resisto em sentir essa dor porque temo que, se eu a deixar entrar, ela me destrua.',
    exemploAgradeco: 'Eu agradeço pela minha força de sentir, pela serenidade que existe dentro de mim e pela minha capacidade de atravessar qualquer dor.',
    exemploSou: 'Eu sou força, serenidade e cura. Eu atravesso a dor e saio mais inteiro. Eu sou o Eu Sou.',
    perguntas: [
      {
        numero: 1,
        pergunta: 'Qual é a dor que você mais resiste em olhar diretamente? O que você teme que aconteça se parar de fugir dela?',
        ajudaReconheco: 'Nomeie a dor e o medo que existe em encará-la.',
        ajudaAgradeco: 'Escreva sobre sua força de sentir, sobre sua capacidade de atravessar qualquer dor e seguir inteiro.',
        ajudaSou: 'Declare quem você é além de qualquer dor, o que permanece quando ela passa.',
      },
      {
        numero: 2,
        pergunta: 'Existe uma mágoa específica que você nunca permitiu processar completamente? Onde ela vive no seu corpo?',
        ajudaReconheco: 'Nomeie a mágoa e onde ela se instala fisicamente.',
        ajudaAgradeco: 'Escreva sobre o alívio que vem quando você permite que a mágoa seja liberada.',
        ajudaSou: 'Declare quem você é como ser que sente, processa e se renova através da experiência.',
      },
      {
        numero: 3,
        pergunta: 'Em que situações o pânico ou o medo aparecem de forma desproporcional? O que eles estão tentando te proteger de sentir?',
        ajudaReconheco: 'Reconheça o padrão do pânico e a dor que ele está tentando evitar.',
        ajudaAgradeco: 'Escreva sobre sua serenidade interior, sobre a paz que existe em você além do medo.',
        ajudaSou: 'Declare quem você é como ser sereno, centrado e capaz de atravessar qualquer tempestade.',
      },
      {
        numero: 4,
        pergunta: 'Se você pudesse se render completamente, sem resistência, ao que está doendo agora, o que você reconheceria que precisa mudar?',
        ajudaReconheco: 'Nomeie o que a resistência tem evitado que você veja com clareza.',
        ajudaAgradeco: 'Escreva sobre a sabedoria que a dor carrega quando você tem coragem de ouvi-la.',
        ajudaSou: 'Declare quem você é como ser que aceita, integra e cresce através de tudo que viveu.',
      },
    ],
    orientacaoPratica: 'Aplique 1 gota de Deep Blue diluído com 10 gotas de óleo de coco fracionado na nuca, ombros e base da coluna nos momentos de dor emocional intensa. Esse óleo é seu convite à rendição — não à derrota, mas à entrega corajosa ao que precisa ser sentido para ser liberado.',
    indicacoesFisicas: [
      { titulo: 'Tópico', texto: 'Diluir 1 gota com 10 gotas de óleo de coco fracionado. Aplicar diluído na área afetada para dores musculares, inflamações, tensão e dores nas articulações.' },
      { titulo: 'Dor de cabeça', texto: 'Aplicar diluído nas têmporas, nuca e ombros para dor de cabeça e enxaqueca.' },
      { titulo: 'Precauções', texto: 'Nunca aplicar em feridos abertos. Nunca ingerir.' },
    ],
    placeholder: 'Escreva livremente o que vier à mente...',
  },
  {
    numero: 10,
    oleo: 'Breathe',
    emoji: '🌬️',
    titulo: 'Respiração',
    tituloCompleto: 'Breathe, Respiração',
    tema: 'abrir o peito para o amor, a confiança e o consolo',
    indicadoPara: 'Tristeza, pesar, desespero, sentimento de não ser amado, constrição, desconfiança e bloqueio emocional.',
    promove: 'Amor, suporte, recepção, abertura, consolo, abraço à vida e confiança.',
    comoUsarAntes: 'Coloque o óleo na palma da mão, feche os olhos e inale profundamente.',
    exemploReconheco: 'Eu reconheço que meu peito está fechado e que sinto dificuldade de receber amor e me deixar ser sustentada.',
    exemploAgradeco: 'Eu agradeço pelo amor que me atravessa, pela abertura do meu coração e pela confiança que tenho na vida.',
    exemploSou: 'Eu sou aberto, amado e sustentada. Eu respiro em plena confiança. Eu sou o Eu Sou.',
    perguntas: [
      {
        numero: 1,
        pergunta: 'Você se sente amado, por si mesmo, pelas pessoas ao seu redor, pelo universo? O que impede de receber esse amor plenamente?',
        ajudaReconheco: 'Reconheça onde o bloqueio para receber amor existe. Seja honesto.',
        ajudaAgradeco: 'Escreva sobre o amor que existe em você e ao seu redor.',
        ajudaSou: 'Declare quem você é como ser profundamente amado, sustentada e apoiada.',
      },
      {
        numero: 2,
        pergunta: 'Qual é a tristeza mais antiga que ainda vive no seu peito? Quando ela chegou pela primeira vez?',
        ajudaReconheco: 'Nomeie a tristeza e sua origem. Deixe aparecer sem pressa.',
        ajudaAgradeco: 'Escreva sobre o consolo que você merece receber, de si mesmo e do campo ao seu redor.',
        ajudaSou: 'Declare quem você é como ser que foi consolada, curada e que segue em amor.',
      },
      {
        numero: 3,
        pergunta: 'Em que momentos você prende a respiração, literal ou metaforicamente, por medo do que pode vir?',
        ajudaReconheco: 'Reconheça os momentos de constrição, de contenção, de não se permitir existir plenamente.',
        ajudaAgradeco: 'Escreva sobre sua abertura, sobre sua capacidade de confiar na vida e de respirar com ela.',
        ajudaSou: 'Declare quem você é como ser aberto, confiante e em plena expansão.',
      },
      {
        numero: 4,
        pergunta: 'O que você mais precisa receber agora, que tipo de suporte, amor ou amparo ainda não chegou até você?',
        ajudaReconheco: 'Nomeie a necessidade com honestidade.',
        ajudaAgradeco: 'Escreva sobre o universo que conspira a seu favor, sobre o suporte que sempre esteve presente.',
        ajudaSou: 'Declare quem você é como ser que sabe pedir, receber e se deixar ser amado.',
      },
    ],
    orientacaoPratica: 'Inale Breathe sempre que sentir o peito fechado, a respiração curta, a tristeza chegando. Use também de manhã, como ritual de abertura para o dia, respirando com a intenção de receber tudo que o dia tem a oferecer.',
    indicacoesFisicas: [
      { titulo: 'Aromático', texto: 'Inalar 1 gota nas mãos, 2 a 3 vezes ao dia, para problemas respiratórios, ansiedade, tristeza e depressão.' },
      { titulo: 'Tópico', texto: 'Diluir 1 gota com 10 gotas de óleo de coco fracionado. Aplicar diluído na garganta, peito ou costas para desconfortos respiratórios, virose, resfriado e gripe.' },
      { titulo: 'Precauções', texto: 'Nunca ingerir o Breathe.' },
    ],
    placeholder: 'Escreva livremente o que vier à mente...',
  },
  {
    numero: 11,
    oleo: 'ZenGest',
    emoji: '🫧',
    titulo: 'Assimilação',
    tituloCompleto: 'ZenGest, Assimilação',
    tema: 'assimilar experiências de vida e renovar o entusiasmo por viver',
    indicadoPara: 'Perda de apetite por comida e vida, dificuldade de assimilar experiências, saturação, apatia e falta de entusiasmo.',
    promove: 'Assimilação de experiências, ajuste, nutrição, entusiasmo e o prazer de participar da própria vida.',
    comoUsarAntes: 'Coloque o óleo na palma da mão, feche os olhos e inale profundamente. Opcionalmente, dilua 1 gota com 10 gotas de óleo de coco fracionado e aplique na área do estômago.',
    exemploReconheco: 'Eu reconheço que estou saturado e que perdi o apetite pela própria vida, sem entusiasmo para o que antes me movia.',
    exemploAgradeco: 'Eu agradeço pelo meu entusiasmo renovado, pela minha capacidade de assimilar e crescer com cada experiência.',
    exemploSou: 'Eu sou ser que digere a vida com sabedoria, prazer e inteireza. Eu sou o Eu Sou.',
    perguntas: [
      {
        numero: 1,
        pergunta: 'Existe alguma experiência recente, ou não tão recente, que você ainda não conseguiu digerir ou assimilar? O que ficou preso?',
        ajudaReconheco: 'Nomeie a experiência e o que ficou emperrado nela.',
        ajudaAgradeco: 'Escreva sobre sua capacidade de digerir a vida, de assimilar o que foi difícil e extrair sabedoria.',
        ajudaSou: 'Declare quem você é como ser que processa, aprende e continua com mais sabedoria e presença.',
      },
      {
        numero: 2,
        pergunta: 'Você ainda tem apetite pela vida, por novidades, por encontros, por experiências? Se não, quando esse apetite foi embora?',
        ajudaReconheco: 'Reconheça a apatia ou o esvaziamento. Quando e como a vida perdeu o sabor?',
        ajudaAgradeco: 'Escreva sobre o entusiasmo que existe dentro de você esperando para ser ativado.',
        ajudaSou: 'Declare quem você é como ser cheio de apetite, curiosidade e prazer de existir.',
      },
      {
        numero: 3,
        pergunta: 'O que na sua vida está te saturando, te enchendo sem nutrir? O que você está consumindo que não te alimenta mais?',
        ajudaReconheco: 'Nomeie o que está saturando e o que ele está te custando em energia e vitalidade.',
        ajudaAgradeco: 'Escreva sobre o que genuinamente nutre você, o que te dá vida, prazer e força.',
        ajudaSou: 'Declare quem você é como ser que escolhe o que o alimenta e libera o que esgota.',
      },
      {
        numero: 4,
        pergunta: 'Se a sua vida fosse uma refeição, ela estaria nutritiva e saborosa ou insossa e difícil de engolir? O que precisa mudar no cardápio?',
        ajudaReconheco: 'Reconheça com honestidade o que está insosso ou indigesto na sua vida agora.',
        ajudaAgradeco: 'Escreva sobre a vida nutritiva, saborosa e plena que você está construindo.',
        ajudaSou: 'Declare quem você é como ser que vive com sabor, nutrição e prazer em cada experiência.',
      },
    ],
    orientacaoPratica: 'Aplique 1 gota do ZenGest com 10 gotas de óleo de coco fracionado sobre o estômago sempre que se sentir saturado, sem entusiasmo ou com dificuldade de assimilar algo. Use também antes de experiências novas ou desafiadoras, como preparação para receber bem o que vai chegar.',
    indicacoesFisicas: [
      { titulo: 'Interno', texto: 'Ingerir: pingue 1 gota em uma colher e tome com água, 2 a 3 vezes ao dia após a refeição, para desconfortos digestivos, diarreia e constipação. Se preferir, use uma cápsula vegetal.' },
      { titulo: 'Tópico', texto: 'Diluir 1 gota com 10 gotas de óleo de coco fracionado. Aplicar diluído no abdômen para desconfortos digestivos.' },
    ],
    placeholder: 'Escreva livremente o que vier à mente...',
  },
  {
    numero: 12,
    oleo: 'Balance',
    emoji: '⚖️',
    titulo: 'Fundamentação',
    tituloCompleto: 'Balance, Fundamentação',
    tema: 'encontrar estabilidade, aterramento e paz mesmo diante das oscilações da vida',
    indicadoPara: 'Estresse, alterações de humor, trauma, medo, luto, instabilidade, dispersão, falta de aterramento e desconexão.',
    promove: 'Calma, paz, relaxamento, aterramento, estabilidade, conexão, comprometimento, independência, força interior e perseverança.',
    comoUsarAntes: 'Aplique 1 a 2 gotas de Balance na área do peito, nuca, pulsos e base da coluna.',
    exemploReconheco: 'Eu reconheço que me sinto instável e sem chão, que os eventos externos me tiram do centro com facilidade.',
    exemploAgradeco: 'Eu agradeço pela minha estabilidade interior, pela paz que existe em mim e pelo aterramento que me sustenta.',
    exemploSou: 'Eu sou raiz, presença e paz profunda. Eu sou meu próprio lar, sempre enraizado e centrado. Eu sou o Eu Sou.',
    perguntas: [
      {
        numero: 1,
        pergunta: 'Em que momento da sua vida você se sente mais instável, disperso ou sem chão? O que ativa essa sensação?',
        ajudaReconheco: 'Nomeie as situações, pessoas ou gatilhos que te tiram do centro.',
        ajudaAgradeco: 'Escreva sobre sua estabilidade interior, sobre o que permanece sólido em você mesmo quando tudo balança.',
        ajudaSou: 'Declare quem você é como ser enraizado, centrado e impossível de ser desequilibrado por completo.',
      },
      {
        numero: 2,
        pergunta: 'Existe algum trauma, pequeno ou grande, que ainda opera como uma raiz solta dentro de você, te impedindo de se sentir fundamentado?',
        ajudaReconheco: 'Nomeie o trauma ou a experiência que ainda te desestabiliza quando tocada.',
        ajudaAgradeco: 'Escreva sobre a cura que já aconteceu e a que ainda está em processo, com gratidão.',
        ajudaSou: 'Declare quem você é além do trauma, quem você é quando está enraizado na sua força.',
      },
      {
        numero: 3,
        pergunta: 'Você consegue se comprometer com algo plenamente, um projeto, uma relação, um caminho, sem escapar quando fica difícil?',
        ajudaReconheco: 'Reconheça onde o padrão de não comprometimento aparece. De onde ele vem?',
        ajudaAgradeco: 'Escreva sobre sua capacidade de permanecer, de perseverar e de honrar seus compromissos.',
        ajudaSou: 'Declare quem você é como ser comprometido, perseverante e confiável consigo mesmo e com o mundo.',
      },
      {
        numero: 4,
        pergunta: 'O que significa para você estar fundamentado? Quando foi a última vez que você se sentiu verdadeiramente em casa em si mesmo?',
        ajudaReconheco: 'Reconheça o que te faz sentir sem chão e o que te falta para se sentir em casa em você.',
        ajudaAgradeco: 'Escreva sobre o lar que existe dentro de você, a paz, a calma e a estabilidade que são suas.',
        ajudaSou: 'Declare quem você é como ser que é seu próprio lar, enraizado, inteiro e em paz.',
      },
    ],
    orientacaoPratica: 'Aplique 1 gota de Balance nos pulsos, no coração e na nuca sempre que se sentir disperso, ansioso, instável ou desconectado de si mesmo. Use de manhã como ritual de aterramento antes de começar o dia. É o óleo do retorno a si.',
    indicacoesFisicas: [
      { titulo: 'Tópico', texto: 'Diluir 1 gota com 10 gotas de óleo de coco fracionado, ou aplicar direto (o Balance já contém óleo de coco fracionado na composição). 1 a 2 gotas na nuca, área do coração ou pulsos para equilíbrio emocional e aterramento. Aplicar diluído na área afetada para dores e inflamações.' },
      { titulo: 'Precauções', texto: 'Nunca ingerir o Balance.' },
    ],
    placeholder: 'Escreva livremente o que vier à mente...',
  },
]

export function getVivencia(numero: number): Vivencia | undefined {
  return VIVENCIAS.find(v => v.numero === Number(numero))
}
